import {Client} from "./internal/model/client_model";
import {Signaling} from "./internal/signaling/interface";
import {WebSocketSignaling} from "./internal/signaling/web_socket";
import {RahatLineWebRTCPeerConnection} from "./internal/webrtc";
import {PromiseCompletionSource, PromiseUsingState} from "./internal/lib/task_completion";
import {SessionDescription} from "./internal/model/session_description";
import {Turn} from "./internal/model/turn";
import {RlAudioStream} from "./media/RlAudioStream";
import {RlMediaStream} from "./media/RlMediaStream";
import {RlVideoStream} from "./media/RlVideoStream";

type Peer = RahatLineWebRTCPeerConnection;

class RahatLineConnection {

    /** @internal */
    private _myId: string = "";
    /** @internal */
    private _signaling: Signaling | undefined;
    /** @internal */
    private _peerConnection: Peer | undefined;
    /** @internal */
    private _connectionState: RTCPeerConnectionState = "new";
    /** @internal */
    private readonly _mediaStreams: Array<RlMediaStream> = [];
    /** @internal */
    private readonly _connectToPeerPromise: PromiseCompletionSource = new PromiseCompletionSource();

    public ClientLeave: ((client: Client) => void) | undefined;
    public NewClientConnected: ((client: Client) => void) | undefined;

    public OnNewAudioStream: ((stream: RlAudioStream) => void) | undefined;
    public OnNewVideoStream: ((stream: RlVideoStream) => void) | undefined;
    public OnVideoStreamClosed: ((stream: RlVideoStream) => void) | undefined;
    public OnAudioStreamClosed: ((stream: RlAudioStream) => void) | undefined;
    public OnConnectionStateChanged: ((state: RTCPeerConnectionState) => void) | undefined;

    /** @internal */
    constructor(signaling: Signaling) {
        this._BootstrapSignaling(signaling);
    }

    public get MyId(): string {
        return this._myId;
    }

    public get ConnectionState(): RTCPeerConnectionState {
        return this._connectionState;
    }

    public get Clients(): ReadonlyArray<Client> {
        return this._signaling.Clients;
    }


    public async StartMicrophoneStream(): Promise<void> {
        if (this.ConnectionState != "connected") throw new Error("Connection state is not connected");

        await this._peerConnection!.StartAudioInputStream();
        await this._StartLocalNegotiation();
    }

    public async StopMicrophoneStream(): Promise<void> {
        if (this.ConnectionState != "connected") throw new Error("Connection state is not connected");

        await this._peerConnection!.StopAudioInputStream();
        await this._StartLocalNegotiation();
    }


    public async StartWebcamStream(): Promise<void> {
        if (this.ConnectionState != "connected") throw new Error("Connection state is not connected");

        await this._peerConnection.StartWebCamStream();
        await this._StartLocalNegotiation()
    }

    public async StopWebcamStream(): Promise<void> {
        if (this.ConnectionState != "connected") throw new Error("Connection state is not connected");

        await this._peerConnection.StopWebCamStream();
        await this._StartLocalNegotiation();
    }


    public async StartCanvasStream(canvas: HTMLCanvasElement, frameRate: number = 15): Promise<void> {
        if (this.ConnectionState != "connected") throw new Error("Connection state is not connected");

        await this._peerConnection.StartStreamCanvas(canvas, frameRate);
        await this._StartLocalNegotiation();
    }

    public async StopCanvasStream(): Promise<void> {
        if (this.ConnectionState != "connected") throw new Error("Connection state is not connected");

        await this._peerConnection.StopStreamCanvas();
        await this._StartLocalNegotiation();
    }

    /**
     * connect to server and return connection ID if connected successfully
     * @constructor
     */
    public async Connect(): Promise<string> {
        try {
            const info = await this._signaling!.Connect();
            this._BootstrapPeerConnection(info.turn);

            await this._StartLocalNegotiation();
            await this._connectToPeerPromise.Promise; // wait for connect to webrtc peer

            this._myId = info.id;
            return info.id;
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    /**
     * Close    * connection to server
     */
    public Close(): void {
        if (this._peerConnection) this._peerConnection.Close();
        if (this._signaling) this._signaling.Close();
    }

    /** @internal */
    // local negotiation means create a new offer and send to server
    private async _StartLocalNegotiation(): Promise<void> {
        const sdp = await this._peerConnection!.Negotiate();
        this._signaling!.SendMyOffer(SessionDescription.Create(sdp));

    }


    /** @internal */
    private _OnIceCandidate_PCEvent(ice: RTCIceCandidate) {
        this._signaling!.SendMyIceCandidate(ice);
    }

    /** @internal */
    private _OnNewTrack_PCEvent(id: string, type: string, stream: MediaStream) {
        if (!id.includes("_")) return;
        const trackClientId = id.split("_")[0];
        const client = this._signaling!.Clients.find(x => x.id == trackClientId);
        if (!client) return;

        if (type.toLowerCase() === "audio") {
            const audioStream = new RlAudioStream(client, id, stream);
            this._mediaStreams.push(audioStream);

            if (!!this.OnNewAudioStream)
                this.OnNewAudioStream(audioStream);
        }

        if (type.toLowerCase() === "video") {
            const videoStream = new RlVideoStream(client, id, stream);
            this._mediaStreams.push(videoStream);

            if (!!this.OnNewVideoStream)
                this.OnNewVideoStream(videoStream)
        }
    }

    /** @internal */
    private _OnRemoveTrack_PCEvent(id: string, type: string) {
        const index = this._mediaStreams.findIndex(x => x.TrackId == id);
        if (index < 0) return;

        if (this._mediaStreams[index] instanceof RlAudioStream) {
            (this._mediaStreams[index] as RlAudioStream).Stop();

            if (!!this.OnAudioStreamClosed) {
                this.OnAudioStreamClosed(this._mediaStreams[index] as RlAudioStream)
            }
        }

        if (this._mediaStreams[index] instanceof RlVideoStream) {
            if (!!this.OnVideoStreamClosed) {
                this.OnVideoStreamClosed(this._mediaStreams[index] as RlVideoStream);
            }
        }

        this._mediaStreams.splice(index, 1);
    }

    /** @internal */
    private _OnConnectionStateChanged_PCEvent(state: RTCPeerConnectionState): void {
        this._connectionState = state;

        if (this._connectToPeerPromise.UsingState == PromiseUsingState.NOT_USED) {
            if (state == "connected") this._connectToPeerPromise.Resolve();
            else if (state != "connecting") this._connectToPeerPromise.Reject("failed to connect");
        }

        if (this.OnConnectionStateChanged) this.OnConnectionStateChanged(state);
    }


    /** @internal */
    private async _OnServerOffer_SignalingEvent(sdp: SessionDescription) {
        const model = sdp.ToRTCSessionDescription();
        const localAnswer = await this._peerConnection!.Negotiate(model);
        this._signaling!.SendMyAnswer(SessionDescription.Create(localAnswer));
    }

    /** @internal */
    private async _OnServerAnswer_SignalingEvent(serverAnswer: SessionDescription) {
        await this._peerConnection!.CompleteLocalNegotiation(serverAnswer.ToRTCSessionDescription());
    }


    /** @internal */
    private _BootstrapSignaling(signaling: Signaling): void {
        this._signaling = signaling;

        this._signaling.OnNewClientJoined = (c) => {
            if (this.NewClientConnected && ((this.MyId ?? '').length > 0 && c.id != this.MyId)) {
                this.NewClientConnected(c);
            }
        };

        this._signaling.OnClientLeave = (c) => {
            if (this.ClientLeave) {
                this.ClientLeave(c);
            }
        };

        this._signaling.OnServerSendOffer = async (serverSdp) => {
            await this._OnServerOffer_SignalingEvent(serverSdp);
        };

        this._signaling.OnServerSendAnswer = async (serverAnswer) => {
            await this._OnServerAnswer_SignalingEvent(serverAnswer);
        }
    }

    /** @internal */
    private _BootstrapPeerConnection(turn: Turn[]): void {
        this._peerConnection = new RahatLineWebRTCPeerConnection(turn, true, true);

        this._peerConnection.OnIceCandidate = (ice) => {
            this._OnIceCandidate_PCEvent(ice);
        }
        this._peerConnection.OnConnectionStateChanged = (s) => {
            this._OnConnectionStateChanged_PCEvent(s);
        }
        this._peerConnection.OnNewTrack = (id, type, stream) => {
            this._OnNewTrack_PCEvent(id, type, stream)
        }
        this._peerConnection.OnRemoveTrack = (id, type) => {
            this._OnRemoveTrack_PCEvent(id, type);
        }
    }

}

/**
 * Initialize
 * Creating a connection class with the RahatLine server
 */
export function InitializeConnection(
    address: string,
    token: string,
    options: Partial<InitializeOptions>
): RahatLineConnection {
    const path = options.path ?? '/ws';
    const port = options.port ?? 8080;
    const proto = options.protocol ?? 'wss';


    if (!Number.isInteger(port)) throw new Error("The port value is not an integer.");
    if (port < 1 || port > 65535) throw new Error("Invalid port number.");

    let signaling = new WebSocketSignaling(address, token, port, path, proto);
    return new RahatLineConnection(signaling);
}

type InitializeOptions = {
    port: number;
    path: string;
    protocol: 'ws' | 'wss';
}